import { createContext, ReactNode, useContext, useState } from 'react'
import { match } from 'ts-pattern'
import { ChartDateShortcut } from '~/features/analytics'
import { BotAnalyticsMetric } from '~/features/bots/types'

type AgentAnalyticsContextValue = {
  dateShortcut: ChartDateShortcut
  setDateShortcut: (dateShortcut: ChartDateShortcut) => void
  selectedMetric: BotAnalyticsMetric
  setSelectedMetric: (metric: BotAnalyticsMetric) => void
  getLabel: (metric: BotAnalyticsMetric) => string
}

type AgentAnalyticsContextProviderProps = {
  children: ReactNode
}

export const AgentAnalyticsContext = createContext<AgentAnalyticsContextValue>({
  dateShortcut: 'lastWeek',
  setDateShortcut: () => {},
  selectedMetric: 'messages',
  setSelectedMetric: () => {},
  getLabel: () => '',
})

export const AgentAnalyticsContextProvider = ({ children }: AgentAnalyticsContextProviderProps) => {
  const [selectedMetric, setSelectedMetric] = useState<BotAnalyticsMetric>('messages')
  const [dateShortcut, setDateShortcut] = useState<ChartDateShortcut>('lastWeek')
  const getLabel = (metric: BotAnalyticsMetric) => {
    return match(metric)
      .with('messages', () => 'Messages')
      .with('newUsers', () => 'Users')
      .with('sessions', () => 'Active Conversations')
      .exhaustive()
  }
  return (
    <AgentAnalyticsContext.Provider
      value={{ dateShortcut, setDateShortcut, selectedMetric, setSelectedMetric, getLabel }}
    >
      {children}
    </AgentAnalyticsContext.Provider>
  )
}

export const useAgentAnalyticsContext = () => {
  const context = useContext(AgentAnalyticsContext)
  if (!context) {
    throw new Error('useAgentAnalyticsContext must be used within an AgentAnalyticsContextProvider')
  }
  return context
}
